import { Text } from '@mantine/core';
import type { AuthoredAgendaSectionVisual } from '../../../../services/api/coachSessionPrep';

/**
 * Plain N/M slide counter rendered BELOW the slide card on the participant
 * active session screen (MED-805 AC #5). Crucially, this is just the counter
 * text — **no Previous/Next arrows** are rendered, since the participant view
 * is read-only and cannot advance content.
 *
 * MED-1030 fix: this counts SLIDES within the current section (current slide /
 * total slides in the section), e.g. `1 / 6` — not sections. The section
 * position is conveyed separately by the "Section X of Y" label in the slide
 * card header. When the current section has no slides the counter is hidden
 * (there is nothing to count). The current slide mirrors the coach-selected
 * main slide (`currentSlidePage`), falling back to the section's first slide —
 * the same rule `SectionSlides` uses to pick the large slide.
 */

export interface ParticipantSlideCounterProps {
  /** The current section's associated slides. */
  visuals: AuthoredAgendaSectionVisual[];
  /**
   * The coach-selected main slide (1-based PDF page); null ⇒ the section's
   * first slide. Read-only here; the coach drives it.
   */
  currentSlidePage: number | null;
}

export function ParticipantSlideCounter({
  visuals,
  currentSlidePage,
}: ParticipantSlideCounterProps) {
  const total = visuals.length;
  if (total === 0) return null;

  const sorted = visuals.slice().sort((a, b) => a.sort_order - b.sort_order);
  const mainIndex =
    currentSlidePage != null
      ? sorted.findIndex(v => v.slide_page === currentSlidePage)
      : -1;
  const current = mainIndex >= 0 ? mainIndex + 1 : 1;

  return (
    <Text
      size="sm"
      ta="center"
      c="rgba(255,255,255,0.55)"
      data-testid="participant-active-slide-counter"
      style={{ paddingTop: 16, paddingBottom: 8, letterSpacing: '0.3px' }}
    >
      {current} / {total}
    </Text>
  );
}
